<template>
  <v-card class="distribution-chart" elevation="1" style="border-radius: 12px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
    <v-card-title class="d-flex align-center justify-space-between">
      <span>{{ title }}</span>
      <v-btn
        v-if="data.length > maxItems"
        small
        text
        color="primary"
        @click="showModal = true"
      >
        Voir tout
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-4" style="min-height: 200px;">
      <div v-if="displayedData.length > 0">
        <highcharts
          ref="chart"
          class="chart"
          :options="chartOptions"
          :update-args="updateArgs"
        />
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state d-flex flex-column align-center justify-center" style="height: 150px;">
        <v-icon large color="grey lighten-2" class="mb-2">mdi-chart-bar</v-icon>
        <p class="text-body-2 text--secondary mb-0">Aucune donnée pour ce filtre</p>
      </div>
    </v-card-text>

    <!-- Modal for "Voir tout" -->
    <v-dialog v-model="showModal" max-width="600px">
      <v-card>
        <v-card-title>{{ title }} - Toutes les données</v-card-title>
        <v-card-text style="max-height: 400px; overflow-y: auto;">
          <div
            v-for="item in data"
            :key="item.name"
            class="bar-item mb-2"
            @click="handleModalItemClick(item)"
            style="cursor: pointer;"
          >
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="bar-label text-body-2">{{ item.name }}</span>
              <span class="bar-value text-body-2 font-weight-bold">
                {{ formatNumber(item.count) }}
              </span>
            </div>
            <div class="bar-background">
              <div
                class="bar-fill"
                :style="{ width: getBarWidth(item.count) }"
              ></div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showModal = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'DistributionChart',
  props: {
    title: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      default: () => []
    },
    maxItems: {
      type: Number,
      default: 8
    }
  },
  data() {
    return {
      showModal: false,
      // Keep chart options stable to enable smooth live updates
      chartOptions: {
        accessibility: { enabled: false },
        chart: { type: 'bar', height: 200 },
        title: { text: null },
        credits: false,
        xAxis: { categories: [], title: { text: null } },
        yAxis: { min: 0, title: { text: null } },
        legend: { enabled: false },
        tooltip: { pointFormat: '<b>{point.y}</b>' },
        plotOptions: {
          series: {
            cursor: 'pointer',
            animation: { duration: 500 },
            dataLabels: { enabled: true }
          }
        },
        series: [{ name: 'Count', data: [], color: '#3B82F6' }]
      },
      updateArgs: [false, true, true]
    };
  },
  computed: {
    displayedData() {
      return [...this.data]
        .sort((a, b) => b.count - a.count)
        .slice(0, this.maxItems);
    },
    maxCount() {
      if (this.data.length === 0) return 1;
      return Math.max(...this.data.map(item => item.count));
    }
  },
  mounted() {
    this.updateChart();
  },
  watch: {
    data: {
      handler() {
        this.updateChart();
      },
      deep: true
    }
  },
  methods: {
    updateChart() {
      const chartRef = this.$refs.chart;
      const chart = chartRef && chartRef.chart;

      const categories = this.displayedData.map(it => it.name);
      const values = this.displayedData.map(it => it.count);

      if (!chart) {
        // First render: set reactive options without recreating chart later
        this.chartOptions.xAxis = { ...this.chartOptions.xAxis, categories };
        this.chartOptions.series = [
          { ...this.chartOptions.series[0], data: values }
        ];
        this.chartOptions.plotOptions = {
          ...this.chartOptions.plotOptions,
          series: {
            ...this.chartOptions.plotOptions.series,
            point: {
              events: {
                click: function() {}
              }
            },
            dataLabels: {
              enabled: true,
              formatter: function() {
                // 'this' is Highcharts point context
                const y = this.y || 0;
                if (y >= 1000) return (y / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
                return y.toLocaleString('fr-FR');
              }
            }
          }
        };
        this.chartOptions.chart = { ...this.chartOptions.chart, height: Math.max(200, 40 + categories.length * 32) };
        return;
      }

      // Update existing chart with animation
      if (chart.xAxis && chart.xAxis[0]) {
        chart.xAxis[0].setCategories(categories, false);
      }
      if (chart.series && chart.series[0]) {
        chart.series[0].setData(values, false, { duration: 500 }, true);
      }

      const self = this;
      chart.update({
        chart: { height: Math.max(200, 40 + categories.length * 32) },
        plotOptions: {
          series: {
            point: {
              events: {
                click: function() {
                  const item = self.displayedData[this.x];
                  if (item) self.$emit('item-click', item);
                }
              }
            }
          }
        }
      }, false, true, { duration: 300 });

      chart.redraw(true);
    },

    getBarWidth(count) {
      if (this.maxCount === 0) return '0%';
      return `${(count / this.maxCount) * 100}%`;
    },

    formatNumber(num) {
      if (num >= 1000) {
        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
      }
      return num.toLocaleString('fr-FR');
    },

    handleModalItemClick(item) {
      this.showModal = false;
      this.$emit('item-click', item);
    }
  }
}
</script>

<style lang="scss" scoped>
.distribution-chart {
  min-height: 320px;
  display: flex;
  flex-direction: column;
}

.bars-container {
  flex: 1;
}

.bar-item {
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.bar-background {
  height: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.bar-fill {
  height: 100%;
  background-color: #3B82F6;
  border-radius: 4px;
  transition: all 0.3s ease-in-out;
  min-width: 2px;

  &:hover {
    background-color: #2563EB;
  }
}

.bar-label {
  font-weight: 500;
}

.bar-value {
  color: #1f2937;
  min-width: fit-content;
  margin-left: 12px;
}

.empty-state {
  color: #9ca3af;

  .v-icon {
    opacity: 0.5;
  }
}

// Modal styling
::v-deep .v-dialog {
  .bar-item:hover {
    background-color: rgba(59, 130, 246, 0.1);
  }
}
</style>